import dagre from "dagre";
import { Edge, Node } from "@xyflow/react";

import { NodeKind, NoteWorkflow, NoteWorkflowsDocument, WorkflowNodeSelection } from "./noteWorkflowsTypes";

const NODE_WIDTH = 220;
const NODE_HEIGHT = 72;
const ROW_GAP = 100;
const SUBFLOW_ROW_GAP = 60;
const SUBFLOW_X_INDENT = NODE_WIDTH;

export interface WorkflowNodeData {
    workflowId: string;
    nodeKind: NodeKind;
    nodeId: string;
    subflowId?: string;
    title: string;
    description: string;
    selected: boolean;
    [key: string]: unknown;
}

function nodeKindForId(workflow: NoteWorkflow, nodeId: string): NodeKind {
    if (workflow.trigger.id === nodeId) {
        return "trigger";
    }
    if (workflow.steps.some(step => step.id === nodeId)) {
        return "step";
    }
    return "outcome";
}

function flowNodeId(workflowId: string, nodeKind: NodeKind, nodeId: string): string {
    return `${workflowId}:${nodeKind}:${nodeId}`;
}

function buildEdgePairs(workflow: NoteWorkflow): Array<[string, string]> {
    const pairs: Array<[string, string]> = [];
    const lastChainId =
        workflow.steps.length > 0
            ? workflow.steps[workflow.steps.length - 1].id
            : workflow.trigger.id;

    if (workflow.steps.length > 0) {
        pairs.push([workflow.trigger.id, workflow.steps[0].id]);
        for (let index = 0; index < workflow.steps.length - 1; index += 1) {
            pairs.push([workflow.steps[index].id, workflow.steps[index + 1].id]);
        }
    }

    for (const outcome of workflow.outcomes) {
        pairs.push([lastChainId, outcome.id]);
    }

    return pairs;
}

function buildSubflowEdgePairs(subflowId: string, stepIds: string[]): Array<[string, string]> {
    if (stepIds.length === 0) {
        return [];
    }
    const pairs: Array<[string, string]> = [[subflowId, stepIds[0]]];
    for (let index = 0; index < stepIds.length - 1; index += 1) {
        pairs.push([stepIds[index], stepIds[index + 1]]);
    }
    return pairs;
}

function isNodeSelected(
    selected: WorkflowNodeSelection | null,
    workflowId: string,
    nodeKind: NodeKind,
    nodeId: string,
    subflowId?: string
): boolean {
    if (!selected || selected.workflowId !== workflowId) {
        return false;
    }
    if (nodeKind === "subflow_step") {
        return (
            selected.nodeKind === "subflow_step" &&
            selected.subflowId === subflowId &&
            selected.nodeId === nodeId
        );
    }
    return selected.nodeKind === nodeKind && selected.nodeId === nodeId;
}

function layoutSubflowRow(
    workflow: NoteWorkflow,
    subflow: NonNullable<NoteWorkflow["subflows"]>[number],
    yOffset: number,
    selected: WorkflowNodeSelection | null,
    nodes: Node<WorkflowNodeData>[],
    edges: Edge[]
): number {
    const graph = new dagre.graphlib.Graph();
    graph.setDefaultEdgeLabel(() => ({}));
    graph.setGraph({ rankdir: "LR", nodesep: 48, ranksep: 72, marginx: 16, marginy: 16 });

    graph.setNode(subflow.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
    for (const step of subflow.steps) {
        graph.setNode(step.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
    }

    const edgePairs = buildSubflowEdgePairs(
        subflow.id,
        subflow.steps.map(step => step.id)
    );
    for (const [source, target] of edgePairs) {
        graph.setEdge(source, target);
    }

    dagre.layout(graph);

    const xIndent = SUBFLOW_X_INDENT;
    let rowMaxY = 0;

    const addSubflowNode = (
        nodeId: string,
        nodeKind: "subflow" | "subflow_step",
        title: string,
        description: string,
        subflowId?: string
    ) => {
        const position = graph.node(nodeId);
        if (!position) {
            return;
        }
        const x = position.x - NODE_WIDTH / 2 + xIndent;
        const relativeY = position.y - NODE_HEIGHT / 2;
        rowMaxY = Math.max(rowMaxY, relativeY + NODE_HEIGHT);
        nodes.push({
            id: flowNodeId(workflow.id, nodeKind, nodeId),
            type: nodeKind,
            position: { x, y: relativeY + yOffset },
            data: {
                workflowId: workflow.id,
                nodeKind,
                nodeId,
                subflowId,
                title,
                description,
                selected: isNodeSelected(selected, workflow.id, nodeKind, nodeId, subflowId)
            },
            selectable: true,
            draggable: false
        });
    };

    addSubflowNode(subflow.id, "subflow", subflow.title || "", subflow.description || "");
    for (const step of subflow.steps) {
        addSubflowNode(step.id, "subflow_step", step.title || "", step.description || "", subflow.id);
    }

    for (const [source, target] of edgePairs) {
        const sourceKind = source === subflow.id ? "subflow" : "subflow_step";
        const targetKind = target === subflow.id ? "subflow" : "subflow_step";
        edges.push({
            id: `${workflow.id}:sf:${subflow.id}:${source}->${target}`,
            source: flowNodeId(workflow.id, sourceKind, source),
            target: flowNodeId(workflow.id, targetKind, target),
            sourceHandle: "right",
            targetHandle: "left",
            type: "smoothstep",
            selectable: false
        });
    }

    return rowMaxY;
}

export function workflowsToFlow(
    document: NoteWorkflowsDocument,
    selected: WorkflowNodeSelection | null
): { nodes: Node<WorkflowNodeData>[]; edges: Edge[] } {
    const nodes: Node<WorkflowNodeData>[] = [];
    const edges: Edge[] = [];
    let yOffset = 0;

    for (const workflow of document.workflows) {
        const graph = new dagre.graphlib.Graph();
        graph.setDefaultEdgeLabel(() => ({}));
        graph.setGraph({ rankdir: "LR", nodesep: 48, ranksep: 72, marginx: 16, marginy: 16 });

        graph.setNode(workflow.trigger.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
        for (const step of workflow.steps) {
            graph.setNode(step.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
        }
        for (const outcome of workflow.outcomes) {
            graph.setNode(outcome.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
        }

        for (const [source, target] of buildEdgePairs(workflow)) {
            graph.setEdge(source, target);
        }

        dagre.layout(graph);

        let rowMaxY = 0;

        const addNode = (
            nodeId: string,
            nodeKind: NodeKind,
            title: string,
            description: string
        ) => {
            const position = graph.node(nodeId);
            if (!position) {
                return;
            }
            const x = position.x - NODE_WIDTH / 2;
            const relativeY = position.y - NODE_HEIGHT / 2;
            rowMaxY = Math.max(rowMaxY, relativeY + NODE_HEIGHT);
            nodes.push({
                id: flowNodeId(workflow.id, nodeKind, nodeId),
                type: nodeKind,
                position: { x, y: relativeY + yOffset },
                data: {
                    workflowId: workflow.id,
                    nodeKind,
                    nodeId,
                    title,
                    description,
                    selected: isNodeSelected(selected, workflow.id, nodeKind, nodeId)
                },
                selectable: true,
                draggable: false
            });
        };

        addNode(workflow.trigger.id, "trigger", workflow.trigger.title || "", workflow.trigger.description || "");
        for (const step of workflow.steps) {
            addNode(step.id, "step", step.title || "", step.description || "");
        }
        for (const outcome of workflow.outcomes) {
            addNode(outcome.id, "outcome", outcome.title || "", outcome.description || "");
        }

        for (const [source, target] of buildEdgePairs(workflow)) {
            edges.push({
                id: `${workflow.id}:${source}->${target}`,
                source: flowNodeId(workflow.id, nodeKindForId(workflow, source), source),
                target: flowNodeId(workflow.id, nodeKindForId(workflow, target), target),
                sourceHandle: "right",
                targetHandle: "left",
                type: "smoothstep",
                selectable: false
            });
        }

        yOffset += rowMaxY + ROW_GAP;

        for (const subflow of workflow.subflows ?? []) {
            const subflowRowHeight = layoutSubflowRow(
                workflow,
                subflow,
                yOffset,
                selected,
                nodes,
                edges
            );
            yOffset += subflowRowHeight + SUBFLOW_ROW_GAP;
        }

        for (const subflow of workflow.subflows ?? []) {
            edges.push({
                id: `${workflow.id}:branch:${workflow.trigger.id}->${subflow.id}`,
                source: flowNodeId(workflow.id, "trigger", workflow.trigger.id),
                target: flowNodeId(workflow.id, "subflow", subflow.id),
                sourceHandle: "bottom",
                targetHandle: "left",
                type: "smoothstep",
                selectable: false
            });
        }
    }

    return { nodes, edges };
}
